/**
 * ActivityReviewActionsPanel — admin surface on a single activity.
 * Phase 2D P0/P1/P2 (2026-04-24)
 *
 * Offers:
 *   - claim / approve (optional note) / reject (reason ≥20) / return (reason ≥20 + flagged_fields) / escalate (reason ≥20)
 *   - ownership reassign (new_owner_agent_id + reason ≥20)
 *   - admin override edit (patch dict + reason ≥20)   [only on approved/rejected]
 *   - dispute resolve (resolution for_agent|for_system + note ≥20)
 *
 * Parent owns the `activity` object + reload hook.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, CheckCircle2, XCircle, Undo2, Flame, Lock, UserCog, ShieldAlert } from "lucide-react";

const OVERRIDE_FIELDS = [
  ["area", "Area"], ["parish", "Parish"], ["visit_outcome", "Outcome"],
  ["conversion_intent", "Intent"], ["notes", "Notes"], ["next_action", "Next action"],
  ["follow_up_date", "Follow-up date"], ["time_started", "Time started"], ["time_ended", "Time ended"],
];

export default function ActivityReviewActionsPanel({ activity, onReload }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // approve|reject|return|escalate|ownership|override|resolve
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [flagged, setFlagged] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [overrideField, setOverrideField] = useState("notes");
  const [overrideValue, setOverrideValue] = useState("");
  const [resolution, setResolution] = useState("for_agent");
  const [resolutionNote, setResolutionNote] = useState("");

  const state = activity?.review?.state;
  const disputeState = activity?.dispute?.state;

  const reset = () => {
    setMode(null); setNote(""); setReason(""); setFlagged("");
    setNewOwner(""); setOverrideValue(""); setResolutionNote("");
  };

  const call = async (path, body) => {
    setBusy(true);
    try {
      const res = await api.post(path, body || {});
      toast.success("Done");
      reset();
      onReload && (await onReload());
      return res;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Action failed");
    } finally { setBusy(false); }
  };

  const id = activity?.id;
  const claim = () => call(`/admin/field-activities/${id}/review/claim`);
  const unclaim = () => call(`/admin/field-activities/${id}/review/unclaim`);
  const approve = () => call(`/admin/field-activities/${id}/review/approve`, note ? { note } : {});
  const reject = () => {
    if (reason.trim().length < 20) return toast.error("Reason must be ≥20 characters");
    return call(`/admin/field-activities/${id}/review/reject`, { reason });
  };
  const returnToAgent = () => {
    if (reason.trim().length < 20) return toast.error("Reason must be ≥20 characters");
    const arr = flagged.split(",").map((s) => s.trim()).filter(Boolean);
    if (arr.length === 0) return toast.error("Specify at least one flagged field");
    return call(`/admin/field-activities/${id}/review/return`, { reason, flagged_fields: arr });
  };
  const escalate = () => {
    if (reason.trim().length < 20) return toast.error("Reason must be ≥20 characters");
    return call(`/admin/field-activities/${id}/review/escalate`, { reason });
  };
  const ownership = async () => {
    if (!newOwner.trim()) return toast.error("Enter new_owner_agent_id");
    if (reason.trim().length < 20) return toast.error("Reason must be ≥20 characters");
    setBusy(true);
    try {
      await api.patch(`/admin/field-activities/${id}/ownership`, { new_owner_agent_id: newOwner.trim(), reason });
      toast.success("Ownership reassigned");
      reset(); onReload && (await onReload());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || "Reassign failed");
    } finally { setBusy(false); }
  };
  const override = () => {
    if (reason.trim().length < 20) return toast.error("Reason must be ≥20 characters");
    if (!overrideValue) return toast.error("New value required");
    return call(`/admin/field-activities/${id}/admin-edit`, {
      patch: { [overrideField]: overrideValue },
      admin_override: true,
      reason,
    });
  };
  const resolve = () => {
    if (resolutionNote.trim().length < 20) return toast.error("Resolution note must be ≥20 characters");
    return call(`/admin/field-activities/${id}/dispute/resolve`, { resolution, resolution_note: resolutionNote });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3" data-testid="admin-review-actions-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="review-state-badge">state: {state || "—"}</Badge>
          {activity?.review?.reviewer_id && <Badge variant="outline">reviewer: {activity.review.reviewer_id}</Badge>}
          {disputeState && <Badge className="bg-fuchsia-600 text-white" data-testid="dispute-state-badge">dispute: {disputeState}</Badge>}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-3">
        {state === "pending" && (
          <Button size="sm" onClick={claim} disabled={busy} data-testid="review-claim-btn">Claim</Button>
        )}
        {state === "under_review" && (
          <>
            <Button size="sm" variant="outline" onClick={unclaim} disabled={busy} data-testid="review-unclaim-btn">Unclaim</Button>
            <Button size="sm" onClick={() => setMode("approve")} data-testid="review-approve-open"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" onClick={() => setMode("reject")} data-testid="review-reject-open"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
            <Button size="sm" variant="outline" onClick={() => setMode("return")} data-testid="review-return-open"><Undo2 className="w-4 h-4 mr-1" /> Return</Button>
            <Button size="sm" variant="outline" onClick={() => setMode("escalate")} data-testid="review-escalate-open"><Flame className="w-4 h-4 mr-1" /> Escalate</Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => setMode("ownership")} data-testid="ownership-reassign-open"><UserCog className="w-4 h-4 mr-1" /> Reassign owner</Button>
        {(state === "approved" || state === "rejected") && (
          <Button size="sm" variant="outline" onClick={() => setMode("override")} data-testid="override-edit-open"><Lock className="w-4 h-4 mr-1" /> Override edit</Button>
        )}
        {disputeState === "open" && (
          <Button size="sm" variant="outline" onClick={() => setMode("resolve")} data-testid="dispute-resolve-open"><ShieldAlert className="w-4 h-4 mr-1" /> Resolve dispute</Button>
        )}
      </div>

      {/* Mode forms */}
      {mode === "approve" && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">Optional note</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-approve-note" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={approve} disabled={busy} data-testid="review-approve-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm approve"}</Button>
          </div>
        </div>
      )}
      {(mode === "reject" || mode === "escalate") && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">Reason (≥20 chars) — {reason.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`review-${mode}-reason`} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={mode === "reject" ? reject : escalate} disabled={busy} data-testid={`review-${mode}-submit`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm ${mode}`}</Button>
          </div>
        </div>
      )}
      {mode === "return" && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">Reason (≥20 chars) — {reason.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-return-reason" />
          <label className="text-xs text-slate-500">Flagged fields (comma-separated)</label>
          <input className="w-full border rounded p-2 text-sm" value={flagged} onChange={(e) => setFlagged(e.target.value)} placeholder="notes, evidence" data-testid="review-return-flagged" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={returnToAgent} disabled={busy} data-testid="review-return-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Return to agent"}</Button>
          </div>
        </div>
      )}
      {mode === "ownership" && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">New owner agent_id</label>
          <input className="w-full border rounded p-2 text-sm font-mono" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} data-testid="ownership-new-owner" />
          <label className="text-xs text-slate-500">Reason (≥20 chars) — {reason.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ownership-reason" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={ownership} disabled={busy} data-testid="ownership-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reassign"}</Button>
          </div>
        </div>
      )}
      {mode === "override" && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">Field</label>
          <select className="w-full border rounded p-2 text-sm" value={overrideField} onChange={(e) => setOverrideField(e.target.value)} data-testid="override-field">
            {OVERRIDE_FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <label className="text-xs text-slate-500">New value</label>
          <input className="w-full border rounded p-2 text-sm" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)} data-testid="override-value" />
          <label className="text-xs text-slate-500">Reason (≥20 chars) — {reason.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-reason" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={override} disabled={busy} data-testid="override-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply override"}</Button>
          </div>
        </div>
      )}
      {mode === "resolve" && (
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <label className="text-xs text-slate-500">Resolution</label>
          <select className="w-full border rounded p-2 text-sm" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="dispute-resolution">
            <option value="for_agent">For agent</option>
            <option value="for_system">For system (against agent)</option>
          </select>
          <label className="text-xs text-slate-500">Resolution note (≥20 chars) — {resolutionNote.length} chars</label>
          <textarea className="w-full border rounded p-2 text-sm" rows={3} value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} data-testid="dispute-resolution-note" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={reset}>Cancel</Button>
            <Button size="sm" onClick={resolve} disabled={busy} data-testid="dispute-resolve-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
